import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { CalendarClock, ChevronLeft, ChevronRight, Clock, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useCanEdit } from '@/lib/auth';
import { intlLocale } from '@/lib/i18n';
import { useTrade } from '@/lib/trade';
import { cn } from '@/lib/utils';
import { MemberAvailabilityDialog } from './member-availability-dialog';
import {
  SCHEDULE_KEY,
  WEEKDAYS,
  addDays,
  fromLocal,
  local,
  scheduleApi,
  weekStart,
  weekdayOf,
  type Board,
  type Member,
  type ScheduledTask,
  type Warning,
} from './schedule-api';

/* ---------------------------------------------------------------------------
   סידור עבודה: שורה לכל עובד, עמודה לכל יום. גוררים עבודה לתא אחר כדי לשנות
   עובד ויום (השעה נשמרת), או לוחצים עליה כדי לערוך שעה ועובד. עבודות בלי
   מועד — בפס למעלה, מוכנות לגרירה.
   --------------------------------------------------------------------------- */

const DEFAULT_VISIT_MIN = 120;
const UNASSIGNED = '__none__';
const dayFmt = new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'numeric', timeZone: 'UTC' });

type Draggable = { id: string; title: string; start: string | null; end: string | null };

export function SchedulePage() {
  const { t } = useTranslation(['schedule', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const trade = useTrade();
  const today = local(new Date()).date;
  const [from, setFrom] = useState(() => weekStart(today));
  const [editing, setEditing] = useState<ScheduledTask | null>(null);
  const [availability, setAvailability] = useState<Member | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  // צפייה בלבד: בלי גרירה ובלי עריכה — לחיצה על עבודה פותחת אותה.
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const openTask = (task: ScheduledTask) =>
    canEdit ? setEditing(task) : void navigate({ to: '/tasks/$taskId', params: { taskId: task.id } });

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(from, i)), [from]);
  const board = useQuery({
    queryKey: [...SCHEDULE_KEY, from],
    queryFn: ({ signal }) => scheduleApi.board(from, signal),
  });

  const move = useMutation({
    mutationFn: (v: { taskId: string; assignedToUserId?: string | null; scheduledStart?: string | null; scheduledEnd?: string | null }) => {
      const { taskId, ...body } = v;
      return scheduleApi.move(taskId, body);
    },
    onSuccess: async ({ warnings }) => {
      if (warnings.length) toast.info(t('page.saved'), warnings.map((w) => describe(w, t)).join(' · '));
      else toast.success(t('page.updated'));
      await qc.invalidateQueries({ queryKey: SCHEDULE_KEY });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (e) => toast.error(t('page.updateFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const data = board.data;
  // עבודות לפי עובד ויום מקומי.
  const cells = useMemo(() => {
    const map = new Map<string, ScheduledTask[]>();
    for (const task of data?.tasks ?? []) {
      if (!task.scheduledStart) continue;
      const key = `${task.assignedToUserId ?? UNASSIGNED}|${local(task.scheduledStart).date}`;
      map.set(key, [...(map.get(key) ?? []), task]);
    }
    return map;
  }, [data]);
  const offOn = (userId: string, date: string) =>
    data?.timeOff.find((off) => off.userId === userId && off.startDate <= date && off.endDate >= date);

  /** גרירה לתא: העובד והיום של התא; השעה של העבודה נשמרת (או תחילת יום העבודה). */
  const dropOn = (member: Member | null, date: string, item: Draggable) => {
    const dayStart = member?.workHours?.[weekdayOf(date)]?.start ?? '09:00';
    const time = item.start ? local(item.start).time : dayStart;
    const minutes = item.start && item.end ? (new Date(item.end).getTime() - new Date(item.start).getTime()) / 60_000 : DEFAULT_VISIT_MIN;
    const start = fromLocal(date, time);
    move.mutate({
      taskId: item.id,
      assignedToUserId: member?.id ?? null,
      scheduledStart: start.toISOString(),
      scheduledEnd: new Date(start.getTime() + minutes * 60_000).toISOString(),
    });
  };

  const onDrop = (member: Member | null, date: string) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    const raw = e.dataTransfer.getData('application/json');
    if (raw) dropOn(member, date, JSON.parse(raw) as Draggable);
  };
  const dragProps = (member: Member | null, date: string) => {
    if (!canEdit) return {};
    const key = `${member?.id ?? UNASSIGNED}|${date}`;
    return {
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(key);
      },
      onDragLeave: () => setDragOver((k) => (k === key ? null : k)),
      onDrop: onDrop(member, date),
      'data-over': dragOver === key || undefined,
    };
  };

  const rows: Array<Member | null> = [...(data?.members ?? []), null];

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description', { worker: trade.worker.one })}
        action={
          <div className="flex items-center gap-1">
            {/* החצים מצביעים לפי כיוון הקריאה: ב-RTL "קודם" מצביע ימינה. */}
            <Button variant="secondary" size="sm" aria-label={t('page.prevWeek')} onClick={() => setFrom(addDays(from, -7))}>
              <ChevronLeft className="rtl:-scale-x-100" aria-hidden />
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setFrom(weekStart(today))}>
              {t('page.thisWeek')}
            </Button>
            <Button variant="secondary" size="sm" aria-label={t('page.nextWeek')} onClick={() => setFrom(addDays(from, 7))}>
              <ChevronRight className="rtl:-scale-x-100" aria-hidden />
            </Button>
          </div>
        }
      />

      {board.isPending ? (
        <Skeleton className="h-96" />
      ) : board.isError ? (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      ) : (
        <>
          {data!.unscheduled.length ? (
            <Card className="p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-fg-muted">
                <CalendarClock className="size-3.5" aria-hidden />
                {t('page.unscheduled', { count: data!.unscheduled.length })}
              </p>
              <ul className="flex gap-2 overflow-x-auto pb-1">
                {data!.unscheduled.map((task) => (
                  <li key={task.id} className="shrink-0">
                    <Chip
                      item={{ id: task.id, title: task.title, start: null, end: null }}
                      customer={task.customer.name}
                      status={task.status}
                      draggable={canEdit}
                      onClick={() => openTask({ ...task, scheduledStart: null, scheduledEnd: null })}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card className="overflow-x-auto">
            <div className="grid min-w-[980px]" style={{ gridTemplateColumns: '11rem repeat(7, minmax(0, 1fr))' }}>
              <div className="sticky start-0 z-10 border-b border-border bg-surface p-2" />
              {days.map((d) => (
                <div
                  key={d}
                  className={cn(
                    'border-b border-s border-border p-2 text-center text-xs',
                    d === today ? 'bg-accent-subtle font-semibold text-accent' : 'text-fg-muted',
                  )}
                >
                  {WEEKDAYS[weekdayOf(d)]} <span className="tabular">{dayFmt.format(new Date(`${d}T12:00:00Z`))}</span>
                </div>
              ))}

              {rows.map((m) => (
                <Row
                  key={m?.id ?? UNASSIGNED}
                  member={m}
                  days={days}
                  today={today}
                  cells={cells}
                  offOn={offOn}
                  dragProps={dragProps}
                  onEdit={openTask}
                  onAvailability={canEdit ? setAvailability : undefined}
                  canEdit={canEdit}
                  workerLabel={trade.worker.one}
                />
              ))}
            </div>
          </Card>
        </>
      )}

      {editing && data ? (
        <EditVisitDialog
          task={editing}
          members={data.members}
          busy={move.isPending}
          onClose={() => setEditing(null)}
          onSave={(v) => move.mutate({ taskId: editing.id, ...v }, { onSuccess: () => setEditing(null) })}
        />
      ) : null}
      {availability && data ? (
        <MemberAvailabilityDialog member={availability} timeOff={data.timeOff} onClose={() => setAvailability(null)} />
      ) : null}
    </div>
  );
}

function Row({
  member,
  days,
  today,
  cells,
  offOn,
  dragProps,
  onEdit,
  onAvailability,
  workerLabel,
  canEdit,
}: {
  member: Member | null;
  days: string[];
  today: string;
  cells: Map<string, ScheduledTask[]>;
  offOn: (userId: string, date: string) => Board['timeOff'][number] | undefined;
  dragProps: (member: Member | null, date: string) => Record<string, unknown>;
  onEdit: (t: ScheduledTask) => void;
  onAvailability?: (m: Member) => void;
  workerLabel: string;
  canEdit: boolean;
}) {
  const { t } = useTranslation('schedule');
  return (
    <>
      <div className="sticky start-0 z-10 flex flex-col justify-center gap-0.5 border-b border-border bg-surface p-2">
        {member ? (
          <>
            <span className="truncate text-sm font-medium text-fg">{member.name}</span>
            <span className="text-2xs text-fg-subtle">
              {member.role === 'FIELD' ? workerLabel : t(ROW_ROLE[member.role])}
            </span>
            {onAvailability ? (
              <button
                type="button"
                className="mt-0.5 inline-flex items-center gap-1 self-start text-2xs text-accent hover:underline"
                onClick={() => onAvailability(member)}
              >
                <Clock className="size-3" aria-hidden />
                {member.workHours ? t('row.hoursAndTimeOff') : t('row.setHours')}
              </button>
            ) : null}
          </>
        ) : (
          <span className="text-xs font-medium text-fg-muted">{t('row.unassigned')}</span>
        )}
      </div>
      {days.map((d) => {
        const off = member ? offOn(member.id, d) : undefined;
        const notWorking = member?.workHours ? !member.workHours[weekdayOf(d)] : false;
        const items = cells.get(`${member?.id ?? UNASSIGNED}|${d}`) ?? [];
        return (
          <div
            key={d}
            {...dragProps(member, d)}
            className={cn(
              'min-h-24 space-y-1 border-b border-s border-border p-1.5 transition-colors data-[over]:bg-accent-subtle',
              d === today && 'bg-accent-subtle/30',
              (off || notWorking) && 'bg-surface-sunken',
            )}
          >
            {off ? <p className="text-2xs font-medium text-fg-muted">{t('row.timeOff')}{off.reason ? ` · ${off.reason}` : ''}</p> : null}
            {!off && notWorking && items.length === 0 ? <p className="text-2xs text-fg-subtle">{t('row.notWorking')}</p> : null}
            {items.map((task) => (
              <Chip
                key={task.id}
                item={{ id: task.id, title: task.title, start: task.scheduledStart, end: task.scheduledEnd }}
                customer={task.customer.name}
                status={task.status}
                draggable={canEdit}
                onClick={() => onEdit(task)}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

/** תווית התפקיד בשורה. FIELD — שם העובד לפי המקצוע (useTrade). */
const ROW_ROLE = {
  OWNER: 'row.owner',
  MANAGER: 'row.manager',
  VIEWER: 'row.viewer',
} as const satisfies Record<Exclude<Member['role'], 'FIELD'>, string>;

const STATUS_STYLE: Record<ScheduledTask['status'], string> = {
  NEW: 'border-accent-border bg-surface text-fg',
  ASSIGNED: 'border-accent-border bg-accent-subtle text-fg',
  IN_PROGRESS: 'border-warning-border bg-warning-subtle text-fg',
  CLOSED: 'border-border bg-surface-sunken text-fg-muted line-through',
  CANCELLED: 'border-border bg-surface-sunken text-fg-subtle',
};

function Chip({
  item,
  customer,
  status,
  draggable: canDrag,
  onClick,
}: {
  item: Draggable;
  customer: string;
  status: ScheduledTask['status'];
  draggable: boolean;
  onClick: () => void;
}) {
  const draggable = canDrag && status !== 'CLOSED' && status !== 'CANCELLED';
  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('application/json', JSON.stringify(item));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onClick}
      className={cn(
        'block w-full max-w-56 rounded-md border px-2 py-1 text-start text-2xs leading-snug shadow-xs',
        draggable && 'cursor-grab active:cursor-grabbing',
        STATUS_STYLE[status],
      )}
    >
      {item.start ? <span className="tabular font-semibold">{local(item.start).time} </span> : null}
      <span className="font-medium">{customer}</span>
      <span className="block truncate text-fg-muted">{item.title}</span>
    </button>
  );
}

/** עריכת ביקור: עובד, תאריך ושעות. גם ביטול שיבוץ או הסרת המועד. */
function EditVisitDialog({
  task,
  members,
  busy,
  onClose,
  onSave,
}: {
  task: ScheduledTask;
  members: Member[];
  busy: boolean;
  onClose: () => void;
  onSave: (v: { assignedToUserId: string | null; scheduledStart: string | null; scheduledEnd: string | null }) => void;
}) {
  const { t } = useTranslation(['schedule', 'common']);
  const trade = useTrade();
  const start = task.scheduledStart ? local(task.scheduledStart) : null;
  const end = task.scheduledEnd ? local(task.scheduledEnd) : null;
  const [assignee, setAssignee] = useState(task.assignedToUserId ?? UNASSIGNED);
  const [date, setDate] = useState(start?.date ?? local(new Date()).date);
  const [from, setFrom] = useState(start?.time ?? '09:00');
  const [to, setTo] = useState(end?.time ?? '11:00');
  const [clearing, setClearing] = useState(false);

  const save = (clearTime = false) => {
    setClearing(clearTime);
    const s = fromLocal(date, from);
    const e = fromLocal(date, to);
    onSave({
      assignedToUserId: assignee === UNASSIGNED ? null : assignee,
      scheduledStart: clearTime ? null : s.toISOString(),
      scheduledEnd: clearTime ? null : (e > s ? e : new Date(s.getTime() + DEFAULT_VISIT_MIN * 60_000)).toISOString(),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="edit-visit-desc">
        <DialogHeader>
          <DialogTitle>{task.customer.name}</DialogTitle>
          <DialogDescription id="edit-visit-desc">{task.title}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label={trade.worker.one} htmlFor="visit-assignee">
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger id="visit-assignee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>{t('edit.noAssignee')}</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t('edit.date')} htmlFor="visit-date">
              <Input id="visit-date" type="date" dir="ltr" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label={t('edit.from')} htmlFor="visit-from">
              <Input id="visit-from" type="time" dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t('edit.to')} htmlFor="visit-to">
              <Input id="visit-to" type="time" dir="ltr" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
          <div className="flex items-center justify-between">
            <Link to="/tasks/$taskId" params={{ taskId: task.id }} className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
              <ExternalLink className="size-3.5" aria-hidden />
              {t('edit.openJob')}
            </Link>
            {task.scheduledStart ? (
              <Button variant="ghost" size="sm" loading={busy && clearing} disabled={busy} onClick={() => save(true)}>
                {t('edit.clearTime')}
              </Button>
            ) : null}
          </div>
          {task.status === 'IN_PROGRESS' ? <Badge tone="warning">{t('edit.inProgress')}</Badge> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={busy && !clearing} disabled={!date || busy} onClick={() => save()}>
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function describe(w: Warning, t: TFunction<'schedule'>): string {
  switch (w.type) {
    case 'conflict':
      return t('warning.conflict', { title: w.title });
    case 'time_off':
      return t('warning.timeOff');
    case 'day_off':
      return t('warning.dayOff');
    case 'outside_hours':
      return t('warning.outsideHours', { detail: w.detail });
  }
}
